import { useEffect, useRef, useState } from "react";

// Candado de pestaña única: garantiza que la app solo esté activa en UNA pestaña
// del mismo navegador. La primera pestaña "posee" el candado (heartbeat en
// localStorage); cualquier pestaña adicional queda bloqueada. Si la pestaña dueña
// se cierra, el candado caduca y una pestaña bloqueada lo retoma automáticamente.
const KEY = "lgmg_tab_lock";
const STALE_MS = 4500;
const HEARTBEAT_MS = 2000;

export default function useSingleTab(enabled = true) {
  const [blocked, setBlocked] = useState(false);
  const blockedRef = useRef(false);
  const tabIdRef = useRef(`${Date.now()}_${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!enabled) return undefined;
    const tabId = tabIdRef.current;
    const now = () => Date.now();
    const read = () => {
      try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
    };
    const claim = () => {
      try { localStorage.setItem(KEY, JSON.stringify({ tabId, ts: now() })); } catch { /* noop */ }
    };
    const setBlockedState = (v) => { blockedRef.current = v; setBlocked(v); };

    // La pestaña MÁS NUEVA toma el control: reclama el candado al montar.
    // La pestaña anterior detectará el cambio y se pausará (newest wins).
    claim();
    setBlockedState(false);

    const hb = setInterval(() => {
      const v = read();
      if (blockedRef.current) {
        // Pausada: si la pestaña dueña se cerró (candado caducó), la retomamos.
        if (!v || now() - v.ts >= STALE_MS) { claim(); setBlockedState(false); }
        return;
      }
      // Somos dueños: si otra pestaña más nueva reclamó, cedemos y nos pausamos.
      if (v && v.tabId !== tabId && now() - v.ts < STALE_MS) { setBlockedState(true); return; }
      claim(); // renovar el latido
    }, HEARTBEAT_MS);

    // Reacción inmediata: si otra pestaña (más nueva) reclama, esta se pausa.
    const onStorage = (e) => {
      if (e.key !== KEY) return;
      const v = read();
      if (!v) return;
      if (!blockedRef.current && v.tabId !== tabId && now() - v.ts < STALE_MS) {
        setBlockedState(true);
      }
    };
    window.addEventListener("storage", onStorage);

    const release = () => {
      if (blockedRef.current) return;
      const v = read();
      if (v && v.tabId === tabId) { try { localStorage.removeItem(KEY); } catch { /* noop */ } }
    };
    window.addEventListener("beforeunload", release);

    return () => {
      clearInterval(hb);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("beforeunload", release);
      release();
    };
  }, [enabled]);

  return blocked;
}
